'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { MainShopProduct } from '@/core/models/shop-product-model';
import { formatPrice } from "@/utils/formatUtils"
import { Heading } from '../../../components/ui/Heading';
import { Badge } from '../../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import ProductHoverMedia from '@/components/shared/ProductHoverMedia';

export default function ProductCard({ product, priority = false }: { product: MainShopProduct; priority?: boolean }) {
  const { dictionary } = useDictionary();
  const inStock = product.inventory && product.inventory > 0;

  // اولویت با thumbnail_hover (که می‌تونه عکس یا ویدئو باشه)؛ اگه نبود،
  // fallback به thumbnail_background.
  const hoverMediaUrl =
    product.thumbnail_hover?.url ||
    (typeof product.thumbnail_background === 'object' && product.thumbnail_background
      ? product.thumbnail_background.url
      : null);


  return (
    <Link
      href={`/shop/product/${product.id}/${product.slug}`}
      className="h-75 p-0 flex flex-col"
    >
      <div className="relative flex-1 overflow-hidden group">
        <Image
          src={product.thumbnail.url}
          alt={product.title}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 25vw"
          className="object-contain transition duration-500 group-hover:scale-105"
          priority={priority}
        />

        {hoverMediaUrl && (
          <ProductHoverMedia url={hoverMediaUrl} alt={product.title} objectFit="cover" />
        )}

        {product.discount_percent > 0 &&
          <Badge variant="destructive" className="absolute top-2 right-2 ltr:left-2 text-xs font-semibold rounded-full px-2.5 py-1 shadow-sm">
            {product.discount_percent}% {dictionary.ui?.cart.discount ?? 'تخفیف'}
          </Badge>}

        {!inStock ?
          <Badge variant="secondary" className="absolute top-2 left-2 ltr:right-2 bg-trasparent text-[16px] font-bold text-gray-400">
            {dictionary.ui?.product.noInventory}
          </Badge> : ""}

      </div>

      <div className="p-2 space-y-2">
        <div className='flex justify-between items-center'>

          <Heading level={3} className="text-xs">
            {product.title}
          </Heading>
          <div className="space-y-1">
            {Number(product.price) === 0 || Number(product.final_price) === 0 ? (
              <p className="text-xs font-medium text-blue-600">{dictionary.ui.form.contactus}</p>
            ) :
              product.discount_percent > 0 ? (
                <div className="flex flex-col items-end space-y-0.5">
                  <span className="text-xs text-red-500 line-through">
                    {formatPrice(Number(product.price))} {dictionary.ui.product.currency}
                  </span>
                  <span className="text-primary-600 text-xs">
                    {formatPrice(Number(product.final_price))} {dictionary.ui.product.currency}
                  </span>
                </div>
              ) : (
                <span className="text-primary-600 text-xs">
                  {formatPrice(Number(product.price))} {dictionary.ui.product.currency}
                </span>
              )}
          </div>
        </div>

        {!!product.product_colors && <div className="flex flex-wrap gap-3">
          {product.product_colors.map((color, index) => (
            <Tooltip key={index}>
              <TooltipTrigger asChild>
                <span>
                  <div
                    className="size-4 rounded-full border border-primary-300 cursor-pointer hover:ring-2 hover:ring-[#C6B8A3] transition-transform hover:scale-110"
                    style={{ backgroundColor: color.color_code_hex }}
                    title={color.color_title}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm text-primary-0">{color.color_title}</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>}
      </div>
    </Link>
  );
}